import * as React from "react";
import {useCallback, useState} from "react";
import {MainContext} from "./mainContext";
import OdxToast from "../../OdxComponents/NativeOdx/Odx-Toast";
import {deepCopy} from "../transformer";

export const MainProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [isLoadingState, setLoadingState] = useState(false);

    const [toast, setToast] = React.useState<IToastDictionary>({})

    const setLoading = useCallback((isLoading: boolean) => {
        setLoadingState(isLoading);
    }, [])

    /**
     * Create a toast
     * @param isDismissable optional, default is true. If true, the toast can be dismissed by the user
     * @param variant optional, default is "default". The variant of the toast. Can be "success", "danger", "warning" or "default"
     * @param title title of the toast
     * @param message message of the toast
     * @param duration duration of the toast in ms. If not set, the toast will not disappear, optional
     */
    const createToast = ({isDismissable = true, variant = "default", title, message, duration = 5000}: IToast) => {
        const keyName = Math.random().toString(36).substring(7) // todo
        const newToast = deepCopy(toast);
        newToast[keyName] = {
            isDismissable,
            variant,
            title,
            message,
            duration
        }
        setToast(newToast)
    }

    /**
     * Create a default error toast
     * @param message message of the toast
     * @param title title of the toast, optional: set to "Error" by default
     */
     const createErrorToast = (message: string, title: string = "Error") => {
        createToast({
            variant: "danger",
            title,
            message,
            duration: 10000
        })
    }

    const removeToast = (key: string) => {
        setToast(prevToast => {
            const newToast = {...prevToast};
            delete newToast[key];
            return newToast;
        });
    }

    const getToast = (): React.ReactElement => {
        const keys = Object.keys(toast)
        if (keys.length === 0) return <></>

        return (<>
            {keys.map((key) => {
                const toastObject = toast[key]
                return <OdxToast
                    key={key}
                    isDismissible={toastObject.isDismissable}
                    variant={toastObject.variant}
                    title={toastObject.title}
                    message={toastObject.message}
                    duration={toastObject.duration}
                    onDismiss={() => removeToast(key)}
                />
            })}
        </>)
    }

    return (
        <MainContext.Provider value={{
            isLoading: isLoadingState,
            setLoading,
            createToast,
            getToast,
            createErrorToast
        }}>
            {children}
        </MainContext.Provider>
    );
}

export interface IToastDictionary {
    [key: string]: IToast
}

export interface IToast {
    isDismissable?: boolean,
    variant?: "success" | "danger" | "warning" | "default",
    title: string,
    message: string,
    duration?: number
}
